/**
 * The unpaid-invoice nudge: which invoices are unpaid enough to interrupt a parent, and whether
 * that interruption is a blocking modal or the banner it collapses into once dismissed.
 *
 * The catalog, a program page and the dashboard all show it, and each used to carry its own copy
 * of these rules. The copies drifted — two asked for every brand's invoices while the third was
 * brand-scoped, so the same banner disagreed with the dashboard beside it (BHCS-127) and fixing
 * that meant making one change twice (BHCS-132). A change to who gets nudged belongs here.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useBrandProfile } from '../contexts/brand-profile';
import type { BrandKey } from '../config/brands';
import type { CampUnpaidInvoiceItem } from '../components/CampPaymentNudge';
import {
  getCampPaymentNudgeScope,
  hasDismissedCampBlockingNudge,
  markCampBlockingNudgeDismissed,
} from '../utils/campPaymentNudgeSession';

interface NudgeInvoice {
  id: string;
  total_amount: number;
  amount_paid: number;
  due_date: string;
  status: string;
  payments?: Array<{ payment_method?: string; status: string }> | null;
}

/** The fields of an enrollment row the nudge reads. The dashboard's richer rows satisfy it as-is. */
export interface NudgeEnrollmentRow {
  is_active: boolean;
  students: { first_name: string; last_name: string } | null;
  programs: { name: string; billing_type: string; brand_key?: string | null };
  invoices: NudgeInvoice[] | null;
}

/**
 * Where the enrollments come from. A page that already holds the parent's enrollments hands them
 * over, so the nudge is computed from the same rows as the list beside it and costs no second
 * request; any other page names the parent and the hook fetches.
 */
export type UnpaidInvoiceNudgeSource =
  | { parentId: string | null | undefined }
  | { enrollments: readonly NudgeEnrollmentRow[] | undefined };

export interface UnpaidInvoiceNudge {
  invoices: CampUnpaidInvoiceItem[];
  /** The blocking modal: there is something to pay and this login session has not dismissed it. */
  showBlocking: boolean;
  /** The banner the modal collapses into once dismissed. */
  showBanner: boolean;
  dismiss: () => void;
}

const UNPAID_STATUSES = ['pending', 'partial', 'overdue'];
const IN_FLIGHT_PAYMENT_STATUSES = ['processing', 'pending', 'created'];

/**
 * The invoices worth interrupting a parent for.
 *
 * `brand` is the catalog being browsed, or null for every brand. Scoped for the same reason the
 * dashboard's "Needs you" list is: the nudge names each program and opens with a count, so
 * unscoped it printed BHCS class names onto the Kaleido catalog and counted a total the page
 * beside it did not. A family does not lose sight of a bill this way — Payments, checkout and the
 * invoice pages are deliberately NOT brand-scoped, because the money is shared even though the
 * catalogs are not. This is an interruption on a catalog page, so it belongs to that catalog.
 */
export function selectUnpaidInvoiceNudges(
  rows: readonly NudgeEnrollmentRow[],
  brand: BrandKey | null,
  today: Date = new Date(),
): CampUnpaidInvoiceItem[] {
  const items: CampUnpaidInvoiceItem[] = [];
  const seenInvoiceIds = new Set<string>();
  for (const row of rows) {
    if (!row.is_active) continue;
    if (brand && row.programs.brand_key !== brand) continue;
    const studentName = row.students ? `${row.students.first_name} ${row.students.last_name}` : undefined;
    for (const invoice of row.invoices ?? []) {
      if (!UNPAID_STATUSES.includes(invoice.status)) continue;
      // For recurring monthly programs, only nudge when the invoice is past due.
      // NOTE: 'overdue' status is defined in the schema but never automatically set by any trigger,
      // cron job, or edge function — invoices stay 'pending'/'partial' indefinitely. Do not rely
      // on status === 'overdue'; use due_date comparison instead.
      if (row.programs.billing_type === 'recurring_monthly' && invoice.status !== 'overdue') {
        if (new Date(invoice.due_date) > today) continue;
      }
      // An ACH payment sits in flight for days; nudging a family to pay again during that window
      // is how they end up paying twice. Card payments complete at once, so only ACH is checked.
      const hasInflightAch = invoice.payments?.some(
        p => p.payment_method === 'ach' && IN_FLIGHT_PAYMENT_STATUSES.includes(p.status.toLowerCase())
      ) ?? false;
      if (hasInflightAch) continue;
      const amountDue = invoice.total_amount - invoice.amount_paid;
      if (amountDue <= 0) continue;
      if (seenInvoiceIds.has(invoice.id)) continue;
      seenInvoiceIds.add(invoice.id);
      items.push({ invoiceId: invoice.id, programName: row.programs.name, studentName, amountDue });
    }
  }
  return items;
}

async function fetchNudgeRows(parentId: string, brand: BrandKey | null): Promise<NudgeEnrollmentRow[]> {
  const { data: studentParents } = await supabase
    .from('student_parents')
    .select('student_id')
    .eq('parent_id', parentId);
  const studentIds = (studentParents ?? []).map(sp => sp.student_id);
  if (studentIds.length === 0) return [];

  const query = supabase
    .from('enrollments')
    .select(`
      is_active,
      students!inner ( first_name, last_name ),
      programs!inner ( name, billing_type, brand_key ),
      invoices!inner (
        id,
        total_amount,
        amount_paid,
        due_date,
        status,
        payments ( payment_method, status )
      )
    `)
    .in('student_id', studentIds)
    .eq('is_active', true)
    .in('invoices.status', UNPAID_STATUSES);

  // Narrows the request to the rows `selectUnpaidInvoiceNudges` keeps anyway. Safe as a filter on
  // an embedded column only because `programs` is an `!inner` embed — without that, PostgREST
  // keeps the row and nulls the embed instead.
  const { data } = await (brand ? query.eq('programs.brand_key', brand) : query);
  return (data ?? []) as unknown as NudgeEnrollmentRow[];
}

function isNudgeItem(item: CampUnpaidInvoiceItem): boolean {
  return typeof item.invoiceId === 'string' &&
    typeof item.programName === 'string' &&
    typeof item.amountDue === 'number' &&
    Number.isFinite(item.amountDue);
}

export function useUnpaidInvoiceNudge(source: UnpaidInvoiceNudgeSource): UnpaidInvoiceNudge {
  const { user, session } = useAuth();
  const { activeBrand, isMultiBrand } = useBrandProfile();
  // One brand at a time while the multi-brand portal is on, everything when it is off.
  const brand = isMultiBrand ? activeBrand : null;
  const handedOver = 'enrollments' in source;
  const prefetched = 'enrollments' in source ? source.enrollments : undefined;
  const parentId = 'parentId' in source ? source.parentId : undefined;

  const { data: fetched } = useQuery<CampUnpaidInvoiceItem[]>({
    queryKey: ['unpaid-camp-invoices', parentId ?? null, brand ?? 'all'],
    queryFn: async () => {
      if (!parentId) return [];
      return selectUnpaidInvoiceNudges(await fetchNudgeRows(parentId, brand), brand);
    },
    enabled: !!parentId,
  });

  const invoices = useMemo(() => {
    const items = handedOver ? selectUnpaidInvoiceNudges(prefetched ?? [], brand) : fetched;
    return Array.isArray(items) ? items.filter(isNudgeItem) : [];
  }, [handedOver, prefetched, fetched, brand]);

  const scope = useMemo(
    () => getCampPaymentNudgeScope(user?.id, session?.refresh_token),
    [user?.id, session?.refresh_token]
  );
  const [dismissed, setDismissed] = useState(false);
  const [dismissedInSession, setDismissedInSession] = useState(false);

  useEffect(() => {
    setDismissed(false);
    setDismissedInSession(hasDismissedCampBlockingNudge(scope));
  }, [scope]);

  const dismiss = () => {
    setDismissed(true);
    setDismissedInSession(true);
    markCampBlockingNudgeDismissed(scope);
  };

  const hasInvoices = invoices.length > 0;
  const wasDismissed = dismissed || dismissedInSession;
  return {
    invoices,
    showBlocking: hasInvoices && !wasDismissed,
    showBanner: hasInvoices && wasDismissed,
    dismiss,
  };
}
